import { Sparkles, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MarkdownView } from "@/lib/markdown";

import { useGenerateCommentary, type LlmCommentary } from "../api";

/**
 * Cinch AI commentary card.
 *
 * Renders the cached LlmCommentary body as markdown (light client-side
 * formatting only — no remark/rehype dependency to keep the bundle thin;
 * we render `# **`-style emphasis with simple CSS rules + preserve
 * newlines).
 *
 * "Regenerate" forces a new LLM call. If the backend hasn't been
 * configured, the body will say so and the regenerate button is a no-op
 * (still useful to try after the user sets env vars).
 */
export function LlmCommentaryCard({
  commentary,
  fiscalYear,
  periodType,
  periodValue,
  isoYear,
  isoWeek,
}: {
  commentary: LlmCommentary | null;
  fiscalYear: number;
  periodType: "month" | "quarter";
  periodValue: number;
  isoYear: number;
  isoWeek: number;
}) {
  const mutation = useGenerateCommentary();

  function handleGenerate(force: boolean) {
    mutation.mutate({
      fiscal_year: fiscalYear,
      period_type: periodType,
      period_value: periodValue,
      scope: "summary",
      force,
      year: isoYear,
      week: isoWeek,
    });
  }

  return (
    <Card className="border-power/40 bg-gradient-to-br from-power/5 to-transparent">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-power" />
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
            Cinch AI commentary
          </CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleGenerate(true)}
          disabled={mutation.isPending}
          title="Regenerate commentary"
        >
          <RefreshCw
            className={`mr-1 h-3.5 w-3.5 ${mutation.isPending ? "animate-spin" : ""}`}
          />
          {commentary ? "Regenerate" : "Generate"}
        </Button>
      </CardHeader>
      <CardContent>
        {!commentary && !mutation.data && (
          <p className="text-sm text-muted-foreground">
            Click <span className="font-semibold">Generate</span> to ask
            Cinch AI for a one-paragraph briefing on this period's numbers.
          </p>
        )}
        {(commentary || mutation.data) && (
          <div className="space-y-2">
            <Body markdown={(mutation.data ?? commentary)!.body} />
            <p className="text-[10px] text-muted-foreground">
              {(mutation.data ?? commentary)!.model_name &&
                `Model: ${(mutation.data ?? commentary)!.model_name} · `}
              Generated{" "}
              {new Date(
                (mutation.data ?? commentary)!.generated_at,
              ).toLocaleString()}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Renders LLM-generated markdown using the shared cinch_ops renderer. */
function Body({ markdown }: { markdown: string }) {
  return <MarkdownView source={markdown} className="text-sm leading-relaxed" />;
}
